import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import useAuthStore from "../store/authStore";
import { useSocket } from "./SocketContext";
import { getNotifications } from "../api/notifications/getAll";
import {
  markNotificationRead,
  markAllNotificationsRead,
} from "../api/notifications/markRead";
import { unsubscribeDeviceOnly } from "../utils/pushNotifications";
import useOpenItem from "../hooks/useOpenItem";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5001";

const NotificationContext = createContext(null);

// A push notification that was clicked while the app was closed opens the
// app with ?notification=<id> (see public/push-sw.js)
const takeLaunchNotificationId = () => {
  const url = new URL(window.location.href);
  const id = url.searchParams.get("notification");
  if (id) {
    url.searchParams.delete("notification");
    window.history.replaceState(window.history.state, "", url);
  }
  return id;
};

// Read once at startup (not during render, which can run more than once)
const launchNotificationId = takeLaunchNotificationId();

export function NotificationProvider({ children }) {
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { token } = useAuthStore();
  const { socket, isConnected } = useSocket();
  const openItem = useOpenItem();
  const launchNotificationIdRef = useRef(launchNotificationId);
  const hadTokenRef = useRef(false);
  const notificationsRef = useRef(notifications);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const refreshNotifications = useCallback(async () => {
    if (!token) return [];
    try {
      const data = await getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
      return data.notifications || [];
    } catch (err) {
      console.error("Error fetching notifications:", err);
      return [];
    }
  }, [token]);

  // Mark a notification as read here; the unread count only goes down if
  // it wasn't already (the socket echo of our own change is a no-op)
  const markReadLocally = useCallback((id) => {
    const wasUnread = notificationsRef.current.some(
      (n) => n._id === id && !n.isRead,
    );
    if (!wasUnread) return;
    notificationsRef.current = notificationsRef.current.map((n) =>
      n._id === id ? { ...n, isRead: true } : n,
    );
    setNotifications(notificationsRef.current);
    setUnreadCount((count) => Math.max(0, count - 1));
  }, []);

  const markAsRead = useCallback(
    async (id) => {
      markReadLocally(id);
      try {
        await markNotificationRead(id);
      } catch (err) {
        console.error("Error marking notification as read:", err);
      }
    },
    [markReadLocally],
  );

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  }, []);

  // Mark a notification as read and open what it is about
  const openNotification = useCallback(
    async (notification) => {
      if (!notification.isRead) markAsRead(notification._id);
      if (!notification.taskId) return;
      await openItem({
        type: "task",
        id: notification.taskId,
        parentTaskId: notification.parentTaskId,
        board: notification.boardId && { id: notification.boardId },
        project: notification.projectId && { id: notification.projectId },
        organization: notification.organizationId && {
          id: notification.organizationId,
        },
      });
    },
    [markAsRead, openItem],
  );

  const openNotificationById = useCallback(
    async (id) => {
      const list = await refreshNotifications();
      const notification = list.find((n) => n._id === id);
      if (notification) {
        await openNotification(notification).catch((err) =>
          console.error("Error opening notification:", err),
        );
      }
    },
    [refreshNotifications, openNotification],
  );

  // Load notifications on login; stop push on this device on logout
  useEffect(() => {
    if (token) {
      hadTokenRef.current = true;
      const launchId = launchNotificationIdRef.current;
      launchNotificationIdRef.current = null;
      if (launchId) {
        openNotificationById(launchId);
      } else {
        refreshNotifications();
      }
    } else {
      setNotifications([]);
      setUnreadCount(0);
      if (hadTokenRef.current) {
        hadTokenRef.current = false;
        unsubscribeDeviceOnly();
      }
    }
  }, [token, refreshNotifications, openNotificationById]);

  // A push notification was clicked while the app was open
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handleMessage = (event) => {
      if (event.data?.type === "open-notification") {
        openNotificationById(event.data.notificationId);
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [openNotificationById]);

  // Live updates (also from the user's other tabs and devices)
  useEffect(() => {
    if (!socket || !isConnected || !token) return;

    const handleCreated = (notification) => {
      setNotifications((prev) => [
        notification,
        ...prev.filter((n) => n._id !== notification._id),
      ]);
      setUnreadCount((count) => count + 1);
    };

    const handleRead = ({ id, all }) => {
      if (all) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
        return;
      }
      markReadLocally(id);
    };

    socket.on("notification:created", handleCreated);
    socket.on("notification:read", handleRead);
    return () => {
      socket.off("notification:created", handleCreated);
      socket.off("notification:read", handleRead);
    };
  }, [socket, isConnected, token, markReadLocally]);

  const refreshNotificationStatus = useCallback(async () => {
    if (!token) {
      setNotificationStatus(null);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/search/urgent`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.overdue?.length > 0) {
          setNotificationStatus("overdue");
        } else if (data.dueToday?.length > 0) {
          setNotificationStatus("today");
        } else {
          setNotificationStatus(null);
        }
      }
    } catch (err) {
      console.error("Error fetching notification status:", err);
    }
  }, [token]);

  const clearNotificationStatus = useCallback(() => {
    setNotificationStatus(null);
  }, []);

  // Listen for socket events that might affect notification status
  useEffect(() => {
    if (!socket || !isConnected || !token) return;

    // Events that might change the notification dot
    // Note: task:updated removed to avoid refreshing during description autosave
    const relevantEvents = [
      "task:created",
      "task:deleted",
      "todoList:created",
      "todoList:updated",
      "todoList:deleted",
    ];

    const handleRelevantEvent = () => {
      // Debounce slightly to avoid too many rapid refreshes
      setTimeout(() => {
        refreshNotificationStatus();
      }, 500);
    };

    // Subscribe to all relevant events
    relevantEvents.forEach((event) => {
      socket.on(event, handleRelevantEvent);
    });

    // Cleanup
    return () => {
      relevantEvents.forEach((event) => {
        socket.off(event, handleRelevantEvent);
      });
    };
  }, [socket, isConnected, token, refreshNotificationStatus]);

  // Refresh periodically (every 5 minutes)
  useEffect(() => {
    if (!token) return;

    refreshNotificationStatus();
    const interval = setInterval(refreshNotificationStatus, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, refreshNotificationStatus]);

  return (
    <NotificationContext.Provider
      value={{
        notificationStatus,
        refreshNotificationStatus,
        clearNotificationStatus,
        notifications,
        unreadCount,
        refreshNotifications,
        markAsRead,
        markAllAsRead,
        openNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
}

export default NotificationContext;
